import {
  $framework,
  AppInterface,
  BaseService,
  equals,
  FrameworkService,
  uuid,
} from "@opendash/core";
import {
  $timeseries,
  DataItemDimensionIdentifierInterface,
} from "@opendash/plugin-timeseries";
import produce from "immer";
import {
  AlarmActionInterface,
  AlarmInterface,
  AlarmWebhookInterface,
  DashboardInterface,
  MonitoringAdapterInterface,
  MonitoringPluginConfig,
  WidgetInterface,
  WidgetPresetInterface,
  WidgetTypeInterface,
} from "..";
import { MonitoringAdapterContext } from "./MonitoringAdapterContext";
import { WidgetContext } from "./states/WidgetContext";
type StateInterface = {
  currentDashboard: string | undefined;
  allDashboards: DashboardInterface[];
  allWidgets: WidgetInterface[];
  allWidgetPresets: WidgetPresetInterface[];
  copyDashboardId: string | undefined;
  loadingDashboard: boolean | false;
  creatingWidgets: boolean | false;
  creatingWidgetsProgress: { current: number; total: number } | null;
  alarms: AlarmInterface[];
  alarmWebhooks: AlarmWebhookInterface[];
  alarmActions: AlarmActionInterface[];
};
export class MonitoringService extends BaseService<StateInterface> {
  private app: AppInterface;
  private adapter: MonitoringAdapterInterface;
  private config: MonitoringPluginConfig;
  private context: MonitoringAdapterContext;
  private framework: FrameworkService;
  private widgetTypes = new Set<WidgetTypeInterface<unknown>>();
  private widgetContextCache = new Map<string, WidgetContext>();
  private autoSortPerformedThisSession = new Set<string>();
  public readonly triggerTypes = [
    "string_change",
    "string_equals",
    "string_equals_not",
    "string_includes",
    "string_includes_not",
    "string_starts_with",
    "string_starts_with_not",
    "string_ends_with",
    "string_ends_with_not",
    "boolean_change",
    "boolean_true",
    "boolean_false",
    "boolean_rising_edge",
    "boolean_falling_edge",
    "number_change",
    "number_equals",
    "number_equals_not",
    "number_gt",
    "number_lt",
    "number_in_range",
    "number_out_of_range",
    "ts_last_value_changed",
    "ts_last_value_received",
  ];
  public readonly alarmRuleTypes = [
    // {
    //   value: "string_change",
    //   label: "opendash:data.trigger.string_change",
    // },
    {
      value: "string_equals",
      label: "opendash:data.trigger.string_equals",
    },
    {
      value: "string_equals_not",
      label: "opendash:data.trigger.string_equals_not",
    },
    {
      value: "string_includes",
      label: "opendash:data.trigger.string_includes",
    },
    {
      value: "string_includes_not",
      label: "opendash:data.trigger.string_includes_not",
    },
    {
      value: "string_starts_with",
      label: "opendash:data.trigger.string_starts_with",
    },
    {
      value: "string_starts_with_not",
      label: "opendash:data.trigger.string_starts_with_not",
    },
    {
      value: "string_ends_with",
      label: "opendash:data.trigger.string_ends_with",
    },
    {
      value: "string_ends_with_not",
      label: "opendash:data.trigger.string_ends_with_not",
    },
    {
      value: "string_equals_ref",
      label: "opendash:data.trigger.string_equals_ref",
    },
    {
      value: "string_equals_not_ref",
      label: "opendash:data.trigger.string_equals_not_ref",
    },
    {
      value: "string_includes_ref",
      label: "opendash:data.trigger.string_includes_ref",
    },
    {
      value: "string_includes_not_ref",
      label: "opendash:data.trigger.string_includes_not_ref",
    },
    {
      value: "string_starts_with_ref",
      label: "opendash:data.trigger.string_starts_with_ref",
    },
    {
      value: "string_starts_with_not_ref",
      label: "opendash:data.trigger.string_starts_with_not_ref",
    },
    {
      value: "string_ends_with_ref",
      label: "opendash:data.trigger.string_ends_with_ref",
    },
    {
      value: "string_ends_with_not_ref",
      label: "opendash:data.trigger.string_ends_with_not_ref",
    },
    // {
    //   value: "boolean_change",
    //   label: "opendash:data.trigger.boolean_change",
    // },
    {
      value: "boolean_true",
      label: "opendash:data.trigger.boolean_true",
    },
    {
      value: "boolean_false",
      label: "opendash:data.trigger.boolean_false",
    },
    {
      value: "boolean_equals_ref",
      label: "opendash:data.trigger.boolean_equals_ref",
    },
    {
      value: "boolean_equals_not_ref",
      label: "opendash:data.trigger.boolean_equals_not_ref",
    },
    // {
    //   value: "boolean_rising_edge",
    //   label: "opendash:data.trigger.boolean_rising_edge",
    // },
    // {
    //   value: "boolean_falling_edge",
    //   label: "opendash:data.trigger.boolean_falling_edge",
    // },
    // {
    //   value: "number_change",
    //   label: "opendash:data.trigger.number_change",
    // },
    {
      value: "number_equals",
      label: "opendash:data.trigger.number_equals",
    },
    {
      value: "number_equals_not",
      label: "opendash:data.trigger.number_equals_not",
    },
    {
      value: "number_gt",
      label: "opendash:data.trigger.number_gt",
    },
    {
      value: "number_lt",
      label: "opendash:data.trigger.number_lt",
    },
    {
      value: "number_in_range",
      label: "opendash:data.trigger.number_in_range",
    },
    {
      value: "number_out_of_range",
      label: "opendash:data.trigger.number_out_of_range",
    },
    {
      value: "number_equals_ref",
      label: "opendash:data.trigger.number_equals_ref",
    },
    {
      value: "number_equals_not_ref",
      label: "opendash:data.trigger.number_equals_not_ref",
    },
    {
      value: "number_gt_ref",
      label: "opendash:data.trigger.number_gt_ref",
    },
    {
      value: "number_lt_ref",
      label: "opendash:data.trigger.number_lt_ref",
    },
    {
      value: "ts_last_value_changed",
      label: "opendash:data.trigger.ts_last_value_changed",
    },
    {
      value: "ts_last_value_received",
      label: "opendash:data.trigger.ts_last_value_received",
    },
  ];
  public readonly actionTypes = ["email", "notification", "webhook"];
  public readonly devices = [
    // {
    //   id: "a",
    //   name: "Device A",
    // },
  ];
  constructor() {
    super({
      initialState: {
        currentDashboard: undefined,
        allDashboards: [],
        allWidgets: [],
        allWidgetPresets: [],
        copyDashboardId: undefined,
        loadingDashboard: false,
        creatingWidgets: false,
        creatingWidgetsProgress: null,
        alarms: [],
        alarmWebhooks: [],
        alarmActions: [],
      },
      cacheHandler: $framework.services.DeviceStorageService,
      cacheKey: "opendash:monitoring-service",
      cacheAllowlist: ["currentDashboard"],
    });
  }
  public async init(configs: MonitoringPluginConfig) {
    if (Array.isArray(configs.widgets)) {
      for (const widget of configs.widgets) {
        this.widgetTypes.add(widget);
      }
    }
    this.adapter = configs.adapter;
    this.context = new MonitoringAdapterContext(this);
    this.initAdapter(this.adapter, this.context);
    (window as any).MonitoringServiceDebug = this;
  }
  public registerWidget(widget: WidgetTypeInterface) {
    this.widgetTypes.add(widget);
  }
  public setConfig(config: MonitoringPluginConfig) {
    this.config = config;
  }
  public getConfig() {
    return this.config;
  }
  public get types(): WidgetTypeInterface[] {
    return Array.from(this.widgetTypes.values());
  }
  public get adapterSupportsDashboardSharing() {
    if (!this.adapter) {
      return false;
    }
    return !!this.adapter.openDashboardSharingDialog;
  }
  public get adapterSupportsWidgetSharing() {
    if (!this.adapter) {
      return false;
    }
    return !!this.adapter.openWidgetSharingDialog;
  }
  public getDashboard(id: string): DashboardInterface {
    return this.store.getState().allDashboards.find((db) => db.id === id);
  }
  public listDashboards(): DashboardInterface[] {
    return this.store.getState().allDashboards;
  }
  public getCurrentDashboard(): DashboardInterface {
    const x = this.store.select((state) =>
      state.allDashboards.find(
        (db) =>
          db.id === state.currentDashboard &&
          db.source === $framework.services.SourceService.getCurrent()?.id
      )
    );
    return x;
  }
  public get isCreatingWidgets(): boolean {
    return this.store.getState().creatingWidgets;
  }
  public get creatingWidgetsProgress(): {
    current: number;
    total: number;
  } | null {
    return this.store.getState().creatingWidgetsProgress;
  }
  public setCurrentDashboard(dashboard: DashboardInterface): void {
    if (!dashboard || !dashboard.id) {
      console.warn("MonitoringService.setCurrentDashboard(db) db.id missing");
      return;
    }
    this.store.update((draft) => {
      draft.currentDashboard = dashboard?.id;
    });
    this.notifySubscribers();

    this.checkAndPerformAutoSort(dashboard.id);
  }
  public async createDashboard(dashboard: DashboardInterface): Promise<string> {
    // if (!dashboard.id) {
    //   console.warn(
    //     "MonitoringService.createDashboard(dashboard) dashboard.id missing"
    //   );
    //   return;
    // }
    const source = $framework.services.SourceService.getCurrent();
    return await this.adapter.createDashboard({
      ...dashboard,
      source: source?.id,
    });
  }
  public async createInitialDashboard(
    dashboard: DashboardInterface
  ): Promise<string> {
    const source = $framework.services.SourceService.getCurrent();
    if (this.getConfig().autoCreateWidgetOptions?.autoCreateWidgets) {
      const presets = this.types
        .map((widgetType) => {
          return widgetType.presets.map((preset) => {
            return {
              id: uuid(),
              isOwner: false,
              isShared: false,
              isReadOnly: true,
              layout: preset.layout || [4, 4],
              ...preset,
              widget: {
                ...preset.widget,
                type: widgetType.type,
              },
            };
          });
        })
        .flat();
      const presetKPI = presets.find((preset) => {
        const widgetTypeToCompare = source.tag?.toLowerCase().includes("tenant")
          ? this.getConfig().autoCreateWidgetOptions
              ?.autoCreateWidgetsForSourceType
          : this.getConfig().autoCreateWidgetOptions?.autoCreateWidgetsType;
        return !preset.isShared && preset.widget.type === widgetTypeToCompare;
      });
      const presetsArray = [];
      if (
        source.tag?.toLowerCase().includes("tenant") &&
        this.getConfig().autoCreateWidgetOptions?.autoCreateWidgetsForSource
      ) {
        if (
          this.getConfig().autoCreateWidgetOptions
            ?.autoCreateWidgetsContentForSource === "dimension"
        ) {
          let listItems = await $timeseries.list();
          let limitNumber = 100;
          if (
            this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets
          ) {
            limitNumber =
              this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets;
          }
          listItems = listItems.slice(0, limitNumber);
          listItems.forEach((dataItem) => {
            let dimensionsNumer = 0;
            dataItem.valueTypes.forEach(() => {
              const dimensionValue = [
                dataItem.source,
                dataItem.id,
                dimensionsNumer,
              ];
              const createPreset = JSON.parse(JSON.stringify(presetKPI));
              createPreset.widget.config = {
                icon: "fa:info",
                _sources: [],
                _items: [],
                _dimensions: [dimensionValue],
              };
              presetsArray.push(createPreset);
              dimensionsNumer++;
            });
          });
        } else if (
          this.getConfig().autoCreateWidgetOptions
            ?.autoCreateWidgetsContentForSource === "source"
        ) {
          let sourceChildList = [];
          $framework.services.SourceService.getAll().forEach(
            (sourceFromList) => {
              if (sourceFromList.parent === source.id) {
                sourceChildList.push(sourceFromList);
              }
            }
          );
          let limitNumber = 100;
          if (
            this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets
          ) {
            limitNumber =
              this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets;
          }
          sourceChildList = sourceChildList.slice(0, limitNumber);
          sourceChildList.forEach((dataItem) => {
            const createPreset = JSON.parse(JSON.stringify(presetKPI));
            createPreset.widget.config = {
              source: dataItem,
            };
            presetsArray.push(createPreset);
          });
        }
      } else if (this.getConfig().autoCreateWidgetOptions?.autoCreateWidgets) {
        if (
          this.getConfig().autoCreateWidgetOptions?.autoCreateWidgetsContent ===
          "dimension"
        ) {
          let listItems = await $timeseries.list();
          let limitNumber = 100;
          if (
            this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets
          ) {
            limitNumber =
              this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets;
          }
          listItems = listItems.slice(0, limitNumber);
          listItems.forEach((dataItem) => {
            let dimensionsNumer = 0;
            dataItem.valueTypes.forEach(() => {
              const dimensionValue = [
                dataItem.source,
                dataItem.id,
                dimensionsNumer,
              ];
              const createPreset = JSON.parse(JSON.stringify(presetKPI));
              createPreset.widget.config = {
                icon: "fa:info",
                _sources: [],
                _items: [],
                _dimensions: [dimensionValue],
              };
              presetsArray.push(createPreset);
              dimensionsNumer++;
            });
          });
        } else if (
          this.getConfig().autoCreateWidgetOptions?.autoCreateWidgetsContent ===
          "source"
        ) {
          let sourceChildList = [];
          $framework.services.SourceService.getAll().forEach(
            (sourceFromList) => {
              if (sourceFromList.parent === source.id) {
                sourceChildList.push(sourceFromList);
              }
            }
          );
          let limitNumber = 100;
          if (
            this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets
          ) {
            limitNumber =
              this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets;
          }
          sourceChildList = sourceChildList.slice(0, limitNumber);
          sourceChildList.forEach((dataItem) => {
            const createPreset = JSON.parse(JSON.stringify(presetKPI));
            createPreset.widget.config = {
              source: dataItem,
            };
            presetsArray.push(createPreset);
          });
        }
      }
      const dashboardIDs = await this.adapter.createDashboard({
        ...dashboard,
        source: source?.id,
      });
      const dashboardCurrent = this.getDashboard(dashboardIDs);
      try {
        this.addPresetsToDashboard(dashboardCurrent, presetsArray);
      } catch (e) {
        console.log(e);
      }
      this.store.update((draft) => {
        draft.currentDashboard = dashboardIDs;
      });
      return dashboardIDs;
    }
    return await this.adapter.createDashboard({
      ...dashboard,
      source: source?.id,
    });
  }
  public async copyDashboard(
    dashboardId: string,
    source: string,
    name: string
  ): Promise<string> {
    if (!dashboardId) {
      console.warn("MonitoringService.copyDashboard() dashboardId missing");
      return;
    }
    if (!source) {
      console.warn("MonitoringService.copyDashboard() source missing");
      return;
    }
    if (!name) {
      console.warn("MonitoringService.copyDashboard() name missing");
      return;
    }
    const dashboard = this.getDashboard(dashboardId);
    const dashboardWidgets = dashboard.widgets.map((id) =>
      this.getWidgetById(id)
    );
    const widgets: string[] = [];
    const widgetMap: Record<string, string> = {};
    for (const widget of dashboardWidgets) {
      const id = await this.createWidget({
        ...widget,
        id: undefined,
      });
      widgets.push(id);
      widgetMap[widget.id] = id;
    }
    const layout = dashboard.layout.map((l) => {
      return { ...l, i: widgetMap[l.i] };
    });
    const id = await this.adapter.createDashboard({
      // @ts-ignore
      id: undefined,
      name,
      source,
      widgets,
      layout,
    });
    await this.closeCopyDashboardDialog();
    return id;
  }
  public async openCopyDashboardDialog(dashboardId: string): Promise<void> {
    this.store.update((draft) => {
      draft.copyDashboardId = dashboardId;
    });
  }
  public async closeCopyDashboardDialog(): Promise<void> {
    this.store.update((draft) => {
      draft.copyDashboardId = undefined;
    });
  }
  public async updateDashboard(dashboard: DashboardInterface): Promise<void> {
    if (!dashboard.id) {
      console.warn(
        "MonitoringService.updateDashboard(dashboard) dashboard.id missing"
      );
      return;
    }
    return await this.adapter.updateDashboard(dashboard);
  }
  public async updateDashboardLayout(
    layout: DashboardInterface["layout"]
  ): Promise<void> {
    const currentDashboard = this.getCurrentDashboard();
    return await this.adapter.updateDashboard({
      ...currentDashboard,
      layout,
    });
  }
  public async deleteDashboard(dashboard: DashboardInterface): Promise<void> {
    if (!dashboard.id) {
      console.warn(
        "MonitoringService.deleteDashboard(dashboard) dashboard.id missing"
      );
      return;
    }
    return await this.adapter.deleteDashboard(dashboard);
  }
  public async openDashboardSharingDialog(dashboard: DashboardInterface) {
    if (!dashboard.id) {
      console.warn(
        "MonitoringService.openDashboardSharingDialog(dashboard) dashboard.id missing"
      );
      return;
    }
    if (!this.adapter.openDashboardSharingDialog) {
      console.warn(
        "MonitoringService.openDashboardSharingDialog(dashboard) adapter.openDashboardSharingDialog not implemented"
      );
      return;
    }
    return await this.adapter.openDashboardSharingDialog(dashboard);
  }
  getWidgetById(id: string) {
    return this.store.getState().allWidgets.find((widget) => widget.id === id);
  }
  public async createWidget(widget: WidgetInterface): Promise<string> {
    return await this.adapter.createWidget(widget);
  }
  public async updateWidget(widget: WidgetInterface): Promise<void> {
    return await this.adapter.updateWidget(widget);
  }
  public async deleteWidget(widget: WidgetInterface): Promise<void> {
    for (const dashboard of this.listDashboards()) {
      const widgetId = widget.id;
      if (dashboard.widgets.includes(widget.id)) {
        const dashboardUpdate = produce(dashboard, (draft) => {
          draft.widgets = draft.widgets.filter((w) => w !== widgetId);
          draft.layout = draft.layout.filter((l) => l.i !== widgetId);
        });
        await this.updateDashboard(dashboardUpdate);
      }
    }
    return await this.adapter.deleteWidget(widget);
  }
  public async openWidgetSharingDialog(widget: WidgetInterface) {
    if (!widget.id) {
      console.warn(
        "MonitoringService.openWidgetSharingDialog(widget) widget.id missing"
      );
      return;
    }
    if (!this.adapter.openWidgetSharingDialog) {
      console.warn(
        "MonitoringService.openWidgetSharingDialog(widget) adapter.openWidgetSharingDialog not implemented"
      );
      return;
    }
    return await this.adapter.openWidgetSharingDialog(widget);
  }
  public async createWidgetPreset(
    preset: Omit<
      WidgetPresetInterface,
      "id" | "isOwner" | "isShared" | "isReadOnly"
    >
  ) {
    if (!this.adapter.createWidgetPreset) {
      console.warn(
        "MonitoringService.createWidgetPreset(widget) adapter.createWidgetPreset not implemented"
      );
      return;
    }
    return await this.adapter.createWidgetPreset(preset);
  }
  public async updateWidgetPreset(preset: WidgetPresetInterface) {
    if (!this.adapter.updateWidgetPreset) {
      console.warn(
        "MonitoringService.updateWidgetPreset(widget) adapter.updateWidgetPreset not implemented"
      );
      return;
    }
    return await this.adapter.updateWidgetPreset(preset);
  }
  public async openWidgetPresetSharingDialog(widget: WidgetPresetInterface) {
    if (!widget.id) {
      console.warn(
        "MonitoringService.openWidgetPresetSharingDialog(widget) widget.id missing"
      );
      return;
    }
    if (!this.adapter.openWidgetPresetSharingDialog) {
      console.warn(
        "MonitoringService.openWidgetPresetSharingDialog(widget) adapter.openWidgetPresetSharingDialog not implemented"
      );
      return;
    }
    return await this.adapter.openWidgetPresetSharingDialog(widget);
  }
  public async addPresetsToDashboard(
    dashboard: DashboardInterface,
    presets: WidgetPresetInterface[]
  ): Promise<void> {
    const widgets = [];
    const layout = [];
    if (presets.length > 1) {
      this.store.update((draft) => {
        draft.loadingDashboard = true;
        draft.creatingWidgets = true;
        draft.creatingWidgetsProgress = { current: 0, total: presets.length };
      });
    }
    let index = 0;
    let upIndex = 1;
    let oldX = 0;
    let oldY = 0;
    for (const preset of presets) {
      this.store.update((draft) => {
        if (draft.creatingWidgetsProgress) {
          draft.creatingWidgetsProgress = {
            current: index,
            total: presets.length,
          };
        }
      });
      // @ts-ignore
      const id = await this.createWidget(preset.widget);
      widgets.push(id);
      let sortedArray = JSON.parse(JSON.stringify(dashboard.layout));
      sortedArray = sortedArray.sort((a, b) => b.y - a.y);
      try {
        if (sortedArray && sortedArray.length > 0) {
          oldY = sortedArray[0].y + 50;
        }
      } catch (e) {
        console.log("Error: ", e);
      }
      if (Array.isArray(preset.layout)) {
        const [h, w] = preset.layout;
        if (oldX + (index == 0 ? 0 : w) > 24 - w) {
          oldX = 0;
          oldY = 9999 + upIndex;
          upIndex++;
          layout.push({ i: id, w, h, x: oldX, y: oldY });
          // oldX is already set correctly, no need to reassign
        } else {
          layout.push({ i: id, w, h, x: oldX + (index == 0 ? 0 : w), y: oldY });
          oldX = oldX + (index == 0 ? 0 : w);
        }
      } else {
        layout.push({ i: id, w: 4, h: 4, x: 0, y: 9999 });
      }
      index++;
      await this.updateDashboard({
        ...dashboard,
        widgets: [...dashboard.widgets, ...widgets],
        layout: [...dashboard.layout, ...layout],
      });
    }
    this.store.update((draft) => {
      draft.loadingDashboard = false;
      draft.creatingWidgets = false;
      draft.creatingWidgetsProgress = null;
    });
  }
  public async setHero(widgetId: string, height?: number) {
    const dashboard = this.getCurrentDashboard();
    const newDashboard = produce(dashboard, (draft) => {
      const heroConfig = JSON.parse(draft.heroWidget || "{}");
      heroConfig.top = {
        widget: widgetId,
        height: height || 20,
      };
      draft.heroWidget = JSON.stringify(heroConfig);
      draft.widgets = draft.widgets.filter((w) => w !== widgetId);
      draft.layout = draft.layout.filter((l) => l.i !== widgetId);
    });
    await this.updateDashboard(newDashboard);
  }
  public async setHeroLeft(widgetId: string, size?: number) {
    const dashboard = this.getCurrentDashboard();
    const newDashboard = produce(dashboard, (draft) => {
      const heroConfig = JSON.parse(draft.heroWidget || "{}");
      heroConfig.left = {
        widget: widgetId,
        width: size || 20,
      };
      draft.heroWidget = JSON.stringify(heroConfig);
      draft.widgets = draft.widgets.filter((w) => w !== widgetId);
      draft.layout = draft.layout.filter((l) => l.i !== widgetId);
    });
    await this.updateDashboard(newDashboard);
  }
  public async resetDashboard(): Promise<void> {
    const currentDashboard = this.getCurrentDashboard();
    if (!currentDashboard) {
      console.warn(
        "MonitoringService.resetDashboard() currentDashboard missing"
      );
      return;
    }
    this.store.update((draft) => {
      draft.loadingDashboard = true;
      draft.creatingWidgets = false;
      draft.creatingWidgetsProgress = null;
    });
    try {
      const widgetsToDelete = currentDashboard.widgets
        .map((widgetId) => this.getWidgetById(widgetId))
        .filter(Boolean);
      for (const widget of widgetsToDelete) {
        await this.adapter.deleteWidget(widget);
      }
      const resetDashboard = produce(currentDashboard, (draft) => {
        draft.widgets = [];
        draft.layout = [];
        draft.heroWidget = null;
      });
      await this.updateDashboard(resetDashboard);
      if (this.getConfig().autoCreateWidgetOptions?.autoCreateWidgets) {
        const source = $framework.services.SourceService.getCurrent();
        const presets = this.types
          .map((widgetType) => {
            return widgetType.presets.map((preset) => {
              return {
                id: uuid(),
                isOwner: false,
                isShared: false,
                isReadOnly: true,
                layout: preset.layout || [4, 4],
                ...preset,
                widget: {
                  ...preset.widget,
                  type: widgetType.type,
                },
              };
            });
          })
          .flat();
        const presetKPI = presets.find((preset) => {
          const widgetTypeToCompare = source.tag
            ?.toLowerCase()
            .includes("tenant")
            ? this.getConfig().autoCreateWidgetOptions
                ?.autoCreateWidgetsForSourceType
            : this.getConfig().autoCreateWidgetOptions?.autoCreateWidgetsType;
          return !preset.isShared && preset.widget.type === widgetTypeToCompare;
        });
        const presetsArray = [];
        if (
          source.tag?.toLowerCase().includes("tenant") &&
          this.getConfig().autoCreateWidgetOptions?.autoCreateWidgetsForSource
        ) {
          if (
            this.getConfig().autoCreateWidgetOptions
              ?.autoCreateWidgetsContentForSource === "dimension"
          ) {
            let listItems = await $timeseries.list();
            let limitNumber = 100;
            if (
              this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets
            ) {
              limitNumber =
                this.getConfig().autoCreateWidgetOptions
                  ?.limitAutoCreateWidgets;
            }
            listItems = listItems.slice(0, limitNumber);
            listItems.forEach((dataItem) => {
              let dimensionsNumer = 0;
              dataItem.valueTypes.forEach(() => {
                const dimensionValue = [
                  dataItem.source,
                  dataItem.id,
                  dimensionsNumer,
                ];
                const createPreset = JSON.parse(JSON.stringify(presetKPI));
                createPreset.widget.config = {
                  icon: "fa:info",
                  _sources: [],
                  _items: [],
                  _dimensions: [dimensionValue],
                };
                presetsArray.push(createPreset);
                dimensionsNumer++;
              });
            });
          } else if (
            this.getConfig().autoCreateWidgetOptions
              ?.autoCreateWidgetsContentForSource === "source"
          ) {
            let sourceChildList = [];
            $framework.services.SourceService.getAll().forEach(
              (sourceFromList) => {
                if (sourceFromList.parent === source.id) {
                  sourceChildList.push(sourceFromList);
                }
              }
            );
            let limitNumber = 100;
            if (
              this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets
            ) {
              limitNumber =
                this.getConfig().autoCreateWidgetOptions
                  ?.limitAutoCreateWidgets;
            }
            sourceChildList = sourceChildList.slice(0, limitNumber);
            sourceChildList.forEach((dataItem) => {
              const createPreset = JSON.parse(JSON.stringify(presetKPI));
              createPreset.widget.config = {
                source: dataItem,
              };
              presetsArray.push(createPreset);
            });
          }
        } else if (
          this.getConfig().autoCreateWidgetOptions?.autoCreateWidgets
        ) {
          if (
            this.getConfig().autoCreateWidgetOptions
              ?.autoCreateWidgetsContent === "dimension"
          ) {
            let listItems = await $timeseries.list();
            let limitNumber = 100;
            if (
              this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets
            ) {
              limitNumber =
                this.getConfig().autoCreateWidgetOptions
                  ?.limitAutoCreateWidgets;
            }
            listItems = listItems.slice(0, limitNumber);
            listItems.forEach((dataItem) => {
              let dimensionsNumer = 0;
              dataItem.valueTypes.forEach(() => {
                const dimensionValue = [
                  dataItem.source,
                  dataItem.id,
                  dimensionsNumer,
                ];
                const createPreset = JSON.parse(JSON.stringify(presetKPI));
                createPreset.widget.config = {
                  icon: "fa:info",
                  _sources: [],
                  _items: [],
                  _dimensions: [dimensionValue],
                };
                presetsArray.push(createPreset);
                dimensionsNumer++;
              });
            });
          } else if (
            this.getConfig().autoCreateWidgetOptions
              ?.autoCreateWidgetsContent === "source"
          ) {
            let sourceChildList = [];
            $framework.services.SourceService.getAll().forEach(
              (sourceFromList) => {
                if (sourceFromList.parent === source.id) {
                  sourceChildList.push(sourceFromList);
                }
              }
            );
            let limitNumber = 100;
            if (
              this.getConfig().autoCreateWidgetOptions?.limitAutoCreateWidgets
            ) {
              limitNumber =
                this.getConfig().autoCreateWidgetOptions
                  ?.limitAutoCreateWidgets;
            }
            sourceChildList = sourceChildList.slice(0, limitNumber);
            sourceChildList.forEach((dataItem) => {
              const createPreset = JSON.parse(JSON.stringify(presetKPI));
              createPreset.widget.config = {
                source: dataItem,
              };
              presetsArray.push(createPreset);
            });
          }
        }
        if (presetsArray.length > 0) {
          await this.addPresetsToDashboard(resetDashboard, presetsArray);
        }
      }
    } finally {
      this.store.update((draft) => {
        if (!draft.creatingWidgets) {
          draft.loadingDashboard = false;
        }
      });
    }
  }
  public async sortWidgetsByValue(valueTypeNames?: string[]): Promise<void> {
    return this._sortWidgetsByValue(valueTypeNames, true);
  }

  private async _sortWidgetsByValue(
    valueTypeNames?: string[],
    setLoadingState = true
  ): Promise<void> {
    const currentDashboard = this.getCurrentDashboard();
    if (!currentDashboard) {
      return;
    }
    if (!this.config?.widgetSorting?.enabled) {
      return;
    }
    const searchValueTypes =
      valueTypeNames || this.config.widgetSorting.valueTypeNames || [];
    if (searchValueTypes.length === 0) {
      return;
    }

    if (setLoadingState) {
      this.store.update((draft) => {
        draft.loadingDashboard = true;
      });
    }
    try {
      const widgets = currentDashboard.widgets
        .map((widgetId) => this.getWidgetById(widgetId))
        .filter(Boolean);
      const widgetValues = await Promise.all(
        widgets.map(async (widget) => {
          let sortValue: number | null = null;
          let hasMatchingValueType = false;
          try {
            if (widget.config) {
              if (
                widget.config._dimensions &&
                Array.isArray(widget.config._dimensions)
              ) {
                for (const [source, itemId, dimension] of widget.config
                  ._dimensions) {
                  try {
                    const dataItem = $timeseries._getOrThrowSync(
                      source,
                      itemId
                    );
                    if (dataItem) {
                      const itemNameMatches = searchValueTypes.some(
                        (searchType) =>
                          dataItem.name &&
                          dataItem.name
                            .toLowerCase()
                            .includes(searchType.toLowerCase())
                      );
                      let dimensionMatches = false;
                      if (
                        dataItem.valueTypes &&
                        dataItem.valueTypes[dimension]
                      ) {
                        const valueType = dataItem.valueTypes[dimension];
                        dimensionMatches = searchValueTypes.some(
                          (searchType) =>
                            (valueType.name &&
                              valueType.name
                                .toLowerCase()
                                .includes(searchType.toLowerCase())) ||
                            (valueType.type &&
                              valueType.type
                                .toLowerCase()
                                .includes(searchType.toLowerCase()))
                        );
                      }
                      const matchesValueType =
                        itemNameMatches || dimensionMatches;
                      if (matchesValueType) {
                        hasMatchingValueType = true;
                        try {
                          const latestValue =
                            $timeseries._getValueOrThrowSync(dataItem);
                          if (latestValue && latestValue.value !== undefined) {
                            if (
                              Array.isArray(latestValue.value) &&
                              latestValue.value[dimension] !== undefined
                            ) {
                              sortValue =
                                parseFloat(latestValue.value[dimension]) || 0;
                            } else if (typeof latestValue.value === "number") {
                              sortValue = latestValue.value;
                            }
                            if (sortValue !== null) break;
                          }
                        } catch (error) {}
                      }
                    }
                  } catch (error) {
                    console.warn(
                      `    Error processing dimension [${source}, ${itemId}, ${dimension}]:`,
                      error
                    );
                  }
                }
              }
              if (
                widget.config._items &&
                Array.isArray(widget.config._items) &&
                sortValue === null
              ) {
                for (const itemRef of widget.config._items) {
                  try {
                    const dataItem = $timeseries._getOrThrowSync(
                      itemRef.source,
                      itemRef.id
                    );
                    if (dataItem && dataItem.valueTypes) {
                      for (
                        let dimension = 0;
                        dimension < dataItem.valueTypes.length;
                        dimension++
                      ) {
                        const valueType = dataItem.valueTypes[dimension];
                        const matchesValueType = searchValueTypes.some(
                          (searchType) =>
                            (valueType.name &&
                              valueType.name
                                .toLowerCase()
                                .includes(searchType.toLowerCase())) ||
                            (valueType.type &&
                              valueType.type
                                .toLowerCase()
                                .includes(searchType.toLowerCase()))
                        );
                        if (matchesValueType) {
                          hasMatchingValueType = true;
                          try {
                            const latestValue =
                              $timeseries._getValueOrThrowSync(dataItem);
                            if (
                              latestValue &&
                              latestValue.value !== undefined
                            ) {
                              if (
                                Array.isArray(latestValue.value) &&
                                latestValue.value[dimension] !== undefined
                              ) {
                                sortValue =
                                  parseFloat(latestValue.value[dimension]) || 0;
                              } else if (
                                typeof latestValue.value === "number"
                              ) {
                                sortValue = latestValue.value;
                              }
                              if (sortValue !== null) break;
                            }
                          } catch (error) {}
                        }
                      }
                      if (sortValue !== null) break;
                    }
                  } catch (error) {}
                }
              }
              if (
                widget.config._sources &&
                Array.isArray(widget.config._sources) &&
                sortValue === null
              ) {
                for (const source of widget.config._sources) {
                  try {
                    const sourceItems = await $timeseries.list();
                    const matchingItems = sourceItems.filter(
                      (item) => item.source === source
                    );
                    for (const dataItem of matchingItems) {
                      if (dataItem.valueTypes) {
                        for (
                          let dimension = 0;
                          dimension < dataItem.valueTypes.length;
                          dimension++
                        ) {
                          const valueType = dataItem.valueTypes[dimension];
                          const matchesValueType = searchValueTypes.some(
                            (searchType) =>
                              (valueType.name &&
                                valueType.name
                                  .toLowerCase()
                                  .includes(searchType.toLowerCase())) ||
                              (valueType.type &&
                                valueType.type
                                  .toLowerCase()
                                  .includes(searchType.toLowerCase()))
                          );
                          if (matchesValueType) {
                            hasMatchingValueType = true;
                            try {
                              const latestValue =
                                $timeseries._getValueOrThrowSync(dataItem);
                              if (
                                latestValue &&
                                latestValue.value !== undefined
                              ) {
                                if (
                                  Array.isArray(latestValue.value) &&
                                  latestValue.value[dimension] !== undefined
                                ) {
                                  sortValue =
                                    parseFloat(latestValue.value[dimension]) ||
                                    0;
                                } else if (
                                  typeof latestValue.value === "number"
                                ) {
                                  sortValue = latestValue.value;
                                }
                                if (sortValue !== null) break;
                              }
                            } catch (error) {
                              console.warn(`    Failed to get value:`, error);
                            }
                          }
                        }
                        if (sortValue !== null) break;
                      }
                    }
                    if (sortValue !== null) break;
                  } catch (error) {
                    console.warn(
                      `    Error processing source ${source}:`,
                      error
                    );
                  }
                }
              }
            } else {
            }
          } catch (error) {}
          const result = {
            widget,
            value: sortValue,
            hasMatchingValueType,
            name: widget.config?.name || widget.id,
          };
          return result;
        })
      );
      const sortedWidgets = widgetValues
        .sort((a, b) => {
          if (a.hasMatchingValueType !== b.hasMatchingValueType) {
            return a.hasMatchingValueType ? -1 : 1;
          }
          if (a.value !== null && b.value !== null) {
            return b.value - a.value;
          }
          if (a.value !== null) return -1;
          if (b.value !== null) return 1;
          return a.name.localeCompare(b.name);
        })
        .map((item) => item.widget);
      const sortedLayout = sortedWidgets.map((widget, index) => {
        const existingLayout = currentDashboard.layout.find(
          (l) => l.i === widget.id
        );
        const cols = 6;
        const x = (index % cols) * 4;
        const y = Math.floor(index / cols) * 4;
        return {
          ...existingLayout,
          i: widget.id,
          x,
          y,
          w: existingLayout?.w || 4,
          h: existingLayout?.h || 4,
        };
      });
      const updatedDashboard = produce(currentDashboard, (draft) => {
        draft.layout = sortedLayout;
      });
      await this.updateDashboard(updatedDashboard);
    } catch (error) {
      console.error("Error sorting widgets by value:", error);
    } finally {
      if (setLoadingState) {
        this.store.update((draft) => {
          draft.loadingDashboard = false;
        });
      }
    }
  }
  public async sortWidgetsByName(): Promise<void> {
    if (!this.config?.widgetSorting?.enabled) {
      console.warn("Widget sorting is disabled in configuration");
      return;
    }
    const currentDashboard = this.getCurrentDashboard();
    if (!currentDashboard) {
      console.warn(
        "MonitoringService.sortWidgetsByName() currentDashboard missing"
      );
      return;
    }
    this.store.update((draft) => {
      draft.loadingDashboard = true;
    });
    try {
      const widgets = currentDashboard.widgets
        .map((widgetId) => this.getWidgetById(widgetId))
        .filter(Boolean);
      const sortedWidgets = [...widgets].sort((a, b) => {
        const nameA = a.config?.name || a.id;
        const nameB = b.config?.name || b.id;
        return nameA.localeCompare(nameB);
      });
      const sortedLayout = sortedWidgets.map((widget, index) => {
        const existingLayout = currentDashboard.layout.find(
          (l) => l.i === widget.id
        );
        const cols = 6;
        const x = (index % cols) * 4;
        const y = Math.floor(index / cols) * 4;
        return {
          ...existingLayout,
          i: widget.id,
          x,
          y,
          w: existingLayout?.w || 4,
          h: existingLayout?.h || 4,
        };
      });
      const updatedDashboard = produce(currentDashboard, (draft) => {
        draft.layout = sortedLayout;
      });
      await this.updateDashboard(updatedDashboard);
    } catch (error) {
      console.error("Error sorting widgets by name:", error);
    } finally {
      this.store.update((draft) => {
        draft.loadingDashboard = false;
      });
    }
  }
  public async sortWidgetsBySpecificValueTypes(
    valueTypeNames: string[]
  ): Promise<void> {
    await this.sortWidgetsByValue(valueTypeNames);
  }
  public isWidgetSortingEnabled(): boolean {
    const enabled = this.config?.widgetSorting?.enabled === true;
    return enabled;
  }
  public getConfiguredValueTypes(): string[] {
    return this.config?.widgetSorting?.valueTypeNames || [];
  }

  public isAutoSortOnFirstRenderEnabled(): boolean {
    return this.config?.widgetSorting?.autoSortOnFirstRender?.enabled === true;
  }

  public getAutoSortSourceTagIncludes(): string[] {
    return (
      this.config?.widgetSorting?.autoSortOnFirstRender?.sourceTagIncludes || []
    );
  }

  public async checkAndPerformAutoSort(dashboardId: string): Promise<void> {
    if (!this.isAutoSortOnFirstRenderEnabled()) {
      return;
    }

    if (!this.config?.widgetSorting?.enabled) {
      return;
    }

    const sourceTagIncludes = this.getAutoSortSourceTagIncludes();
    if (sourceTagIncludes.length === 0) {
      return;
    }

    if (this.autoSortPerformedThisSession.has(dashboardId)) {
      return;
    }

    try {
      const dataSources = await $timeseries.list();
      const matchingSources = dataSources.filter((item) => {
        if (!item.source) return false;
        return sourceTagIncludes.some((tagInclude) =>
          item.source.toLowerCase().includes(tagInclude.toLowerCase())
        );
      });

      if (matchingSources.length > 0) {
        this.store.update((draft) => {
          draft.loadingDashboard = true;
        });

        // Wait for dashboard to be fully loaded, then sort
        this.waitForDashboardReady().then(async () => {
          try {
            await this._sortWidgetsByValue(undefined, false);
            this.autoSortPerformedThisSession.add(dashboardId);
          } catch (error) {
            console.warn("Error during auto-sort:", error);
          } finally {
            this.store.update((draft) => {
              draft.loadingDashboard = false;
            });
          }
        });
      }
    } catch (error) {
      console.warn("Error checking sources for auto-sort:", error);
    }
  }

  public resetAutoSortStatus(dashboardId?: string): void {
    if (dashboardId) {
      this.autoSortPerformedThisSession.delete(dashboardId);
    } else {
      this.autoSortPerformedThisSession.clear();
    }
  }

  private async waitForDashboardReady(): Promise<void> {
    return new Promise((resolve) => {
      const checkReady = () => {
        const currentDashboard = this.getCurrentDashboard();
        if (!currentDashboard) {
          setTimeout(checkReady, 100);
          return;
        }

        const widgets = currentDashboard.widgets
          .map((widgetId) => this.getWidgetById(widgetId))
          .filter(Boolean);

        if (widgets.length === currentDashboard.widgets.length) {
          setTimeout(resolve, 300);
        } else {
          setTimeout(checkReady, 100);
        }
      };
      checkReady();
    });
  }

  public debugSortingConfig(): void {
    console.log("🔧 Widget Sorting Configuration:", {
      enabled: this.isWidgetSortingEnabled(),
      valueTypeNames: this.getConfiguredValueTypes(),
      autoSortEnabled: this.isAutoSortOnFirstRenderEnabled(),
      sourceTagIncludes: this.getAutoSortSourceTagIncludes(),
    });
  }

  public async debugAvailableSources(): Promise<void> {
    try {
      const dataSources = await $timeseries.list();
      const sources = [...new Set(dataSources.map((item) => item.source))];
      console.log("🗂️ Available sources:", sources);

      const sourceTagIncludes = this.getAutoSortSourceTagIncludes();
      const matchingSources = sources.filter((source) =>
        sourceTagIncludes.some((tagInclude) =>
          source?.toLowerCase().includes(tagInclude.toLowerCase())
        )
      );
      console.log("✅ Matching sources for auto-sort:", matchingSources);
    } catch (error) {
      console.warn("Error getting sources:", error);
    }
  }
  public getHeroWidget(dashboard: DashboardInterface) {
    const dashboardHeroString = dashboard?.heroWidget;
    let dashboardHero = null as {
      top: { widget: string; height: number };
      left: { widget: string; width: number };
    };
    try {
      dashboardHero = JSON.parse(dashboardHeroString);
    } catch (e) {}
    const { UserService } = $framework.services;
    let defaultHeroConfig = null as {
      top: { widget: string; height: number };
      left: { widget: string; width: number };
    };
    try {
      defaultHeroConfig = JSON.parse(
        UserService.getConfig("MONITORING_DEFAULT_HERO") || "{}"
      );
    } catch (e) {}
    const widgetId =
      dashboardHero?.top?.widget || defaultHeroConfig?.top?.widget || null;
    const widgets = this.store.getState().allWidgets;
    const widget = widgets.find((w) => w.id === widgetId);
    if (!widget) {
      return undefined;
    }
    return dashboardHero?.top || defaultHeroConfig.top;
  }
  public getHeroWidgetLeft(dashboard: DashboardInterface) {
    const dashboardHeroString = dashboard?.heroWidget;
    let dashboardHero = null as {
      top: { widget: string; height: number };
      left: { widget: string; width: number };
    };
    try {
      dashboardHero = JSON.parse(dashboardHeroString);
    } catch (e) {}
    const { UserService } = $framework.services;
    let defaultHeroConfig = null as {
      top: { widget: string; height: number };
      left: { widget: string; width: number };
    };
    try {
      defaultHeroConfig = JSON.parse(
        UserService.getConfig("MONITORING_DEFAULT_HERO_LEFT") || "{}"
      );
    } catch (e) {}
    const widgetId =
      dashboardHero?.left?.widget || defaultHeroConfig?.left?.widget || null;
    const widgets = this.store.getState().allWidgets;
    const widget = widgets.find((w) => w.id === widgetId);
    if (!widget) {
      return undefined;
    }
    return dashboardHero?.left || defaultHeroConfig.left;
  }
  public clearWidgetContextCache() {
    this.widgetContextCache.clear();
  }
  public createWidgetContext(id: string) {
    if (this.widgetContextCache.has(id)) {
      return this.widgetContextCache.get(id);
    }
    const context = new WidgetContext(this, id);
    this.widgetContextCache.set(id, context);
    return context;
  }
  public createWidgetDraftContext() {
    return new WidgetContext(this);
  }
  getAlarm(id: string): AlarmInterface {
    return this.store.select((state) =>
      state.alarms.find((alarm) => alarm.id === id)
    );
  }
  listAlarms(): AlarmInterface[] {
    return this.store.select((state) => state.alarms);
  }
  listAlarmsForItem(
    item: DataItemDimensionIdentifierInterface
  ): AlarmInterface[] {
    try {
      return this.listAlarms().filter((alarm) => equals(alarm.item, item));
    } catch (error) {
      return [];
    }
  }
  async createAlarm(alarm: Omit<AlarmInterface, "id">): Promise<string> {
    return await this.adapter.createAlarm(alarm);
  }
  async updateAlarm(alarm: AlarmInterface): Promise<void> {
    return await this.adapter.updateAlarm(alarm);
  }
  async deleteAlarm(alarm: AlarmInterface): Promise<void> {
    return await this.adapter.deleteAlarm(alarm);
  }
  listWebhooks(): AlarmWebhookInterface[] {
    return this.store.select((state) => state.alarmWebhooks);
  }
  listAlarmActions(): AlarmActionInterface[] {
    return this.store.select((state) => state.alarmActions);
  }
}
